import { useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

export const CRITERIA = [
  ["dialogue_removal", "Dialogue removal"], ["music_removal", "Music removal"], ["effects_preservation", "Effects preservation"],
  ["foley_preservation", "Foley preservation"], ["ambience_preservation", "Ambience preservation"], ["artefacts", "Artefacts (5 = none)"],
  ["overall_me_usefulness", "Overall M&E usefulness"],
];

const Score = ({ value, onChange, testId }) => (
  <div className="flex gap-0.5" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} onClick={() => onChange(n)} className={`h-6 w-6 rounded-sm text-[11px] font-mono border transition-colors ${value === n ? "bg-cyan-500 text-black border-cyan-500" : "border-[#253044] text-slate-400 hover:bg-[#1E2738]"}`}>{n}</button>
    ))}
  </div>
);

// Manual QC ratings grid: engines as columns, criteria as rows. Saves per job via PUT /qc/{job}.
export const BenchmarkRatings = ({ jobs, onSaved }) => {
  const completed = jobs.filter((j) => j.status === "completed");
  const [local, setLocal] = useState(() => Object.fromEntries(completed.map((j) => [j.id, { ratings: j.qc_ratings || {}, notes: j.qc_ratings_notes || "" }])));
  if (!completed.length) return null;
  const set = (jid, patch) => setLocal((l) => ({ ...l, [jid]: { ...l[jid], ...patch } }));
  const save = async (j) => {
    try {
      await api.put(`/qc/${j.id}`, { ratings: local[j.id].ratings, ratings_notes: local[j.id].notes });
      toast.success(`Ratings saved for ${j.engine_name}`);
      onSaved?.();
    } catch (e) { toast.error(errorText(e)); }
  };
  const avg = (jid) => { const v = Object.values(local[jid]?.ratings || {}).filter(Boolean); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : "—"; };
  return (
    <div data-testid="benchmark-ratings" className="panel overflow-x-auto">
      <div className="px-4 py-2.5 micro">Manual QC ratings · 1 poor → 5 excellent</div>
      <table className="w-full text-sm">
        <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-3 py-2">Criterion</th>{completed.map((j) => <th key={j.id} className="px-3 py-2">{j.engine_name}</th>)}</tr></thead>
        <tbody>
          {CRITERIA.map(([k, label]) => (
            <tr key={k} className="border-b border-[#1c2433]">
              <td className="px-3 py-1.5 text-slate-300">{label}</td>
              {completed.map((j) => <td key={j.id} className="px-3 py-1.5"><Score value={local[j.id]?.ratings?.[k]} onChange={(n) => set(j.id, { ratings: { ...local[j.id].ratings, [k]: n } })} testId={`rating-${j.id}-${k}`} /></td>)}
            </tr>
          ))}
          <tr className="border-b border-[#1c2433]"><td className="px-3 py-1.5 text-slate-300">Notes</td>
            {completed.map((j) => <td key={j.id} className="px-3 py-1.5"><textarea data-testid={`rating-notes-${j.id}`} rows={2} value={local[j.id]?.notes || ""} onChange={(e) => set(j.id, { notes: e.target.value })} className="w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-cyan-500" /></td>)}</tr>
          <tr><td className="px-3 py-2 micro">Average · save</td>
            {completed.map((j) => <td key={j.id} className="px-3 py-2 flex items-center gap-3"><span data-testid={`rating-avg-${j.id}`} className="font-mono text-emerald-300">{avg(j.id)}</span><button data-testid={`rating-save-${j.id}`} onClick={() => save(j)} className="px-2.5 h-7 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-xs font-semibold">Save</button>{j.qc_rated_by && <span className="text-[10px] text-slate-500">by {j.qc_rated_by}</span>}</td>)}</tr>
        </tbody>
      </table>
    </div>
  );
};
